import type { TrackedEvent } from '@core/events/tracked-event'
import type { DomainConfig } from '@core/types/domain-config.ts'
import { DataStoreName, StorageType, TimestampedDataValue } from '@core/storage/abstract-data-store.ts'
import { AbstractStorageManager } from './abstract-storage-manager'
import { Logger, TaggedLogger } from '@core/logging/logger'
import { SessionStorageKey, SettingsStorageKey } from '@core/storage/keys.ts'
import type { DomainSettings } from '../types/domain-settings.ts'
import { Globals } from '@core/globals.ts'
import { reduceMap } from '../utils/reduce-map.ts'
import { timeIntervalFromNow } from '@core/utils/time.ts'

/** @internal */
export type SettingsStore = {
    // region General
    _getDomainSettings(): Promise<TimestampedDataValue<DomainSettings> | null>
    _setDomainSettings(settings: DomainSettings): Promise<DomainSettings | null>
    _getDomainConfig(): Promise<TimestampedDataValue<DomainConfig> | null>
    _setDomainConfig(config: DomainConfig): Promise<DomainConfig | null>
    _getCachedEvents(): Promise<TrackedEvent[]>
    _setCachedEvents(events: TrackedEvent[]): Promise<TrackedEvent[]>
    _getWriteAheadEvents(): Promise<TrackedEvent[]>
    _addWriteAheadEvent(event: TrackedEvent): Promise<boolean>
    _removeWriteAheadEvent(eventId: string): Promise<void>
    // endregion

    // region Prompts
    _getViewedPromptOccurrencesCount(): Promise<number>
    /** Returns Map<PromptId, Timestamp[]> */
    _getViewedPromptOccurrencesMap(): Promise<Map<number, number[]>>
    _dropViewedPromptOccurrencesNotInInterval(intervalMs: number): Promise<void>
    _incrementViewedOccurrencesForPromptId(id: number): Promise<void>
    _getLastViewedPromptId(): Promise<number | null>
    _setLastViewedPromptId(id: number): Promise<number | null>
    _setLastMatchedPromptId(promptId: number | null): Promise<number | null>
    _getLastMatchedPromptId(): Promise<number | null>
    // endregion
}

const idbAndMemStoresOnly: [StorageType, ...StorageType[]] = [StorageType.IDB, StorageType.MEM]
/**
 * IndexedDB alone: a write-ahead event exists to survive the page, so a copy held only in memory
 * would report as stored when it is not.
 */
const writeAheadEventStores: [StorageType, ...StorageType[]] = [StorageType.IDB]
const sessionStoresOnly: [StorageType, ...StorageType[]] = [StorageType.SESSION, StorageType.MEM]

/**
 * Stores backing the merged settings document.
 *
 * Both durable stores are written, because neither alone is sufficient. sessionStorage is
 * available in contexts where IndexedDB is not — Firefox private browsing, partitioned
 * third-party contexts, storage pressure — and `IdbDataStore` surfaces a blocked or aborted
 * transaction as a plain read miss, which the caller cannot distinguish from an expired
 * entry. With IndexedDB as the only durable store, any of those turned a cached read into a
 * refetch, and since the in-memory store is per-page-load it could not cover for it. That is
 * what made domain config refetch inside its own TTL far more often than prompts did on an
 * identical TTL.
 *
 * IndexedDB is still written: it outlives the tab, and the service worker can only read it.
 *
 * Reads take the newest entry across stores rather than the first, so disagreement between
 * them resolves to the most recent write.
 */
const settingsDocumentStores: [StorageType, ...StorageType[]] = [StorageType.SESSION, StorageType.IDB, StorageType.MEM]

/** @internal */
export class SettingsStorageManager extends AbstractStorageManager implements SettingsStore {
    /**
     * The tail of this tab's write-ahead list changes. Each read-modify-write starts only once the
     * previous one has finished, so an add and a remove in this tab cannot overwrite each other.
     * It does not make the list safe across tabs.
     */
    private writeAheadEventsChange: Promise<unknown> = Promise.resolve()

    public constructor(logger?: Logger) {
        super(
            DataStoreName.sdk,
            [
                // SDK settings should not be stored via the COOKIE
                // storage type due to value size
                StorageType.MEM,
                StorageType.IDB,
                StorageType.SESSION,
            ],
            logger ?? TaggedLogger._getInstance('SettingsStore'),
            // Settings hold the largest value the SDK persists, and the session manager writes
            // its own blob every second, so they do not share one.
            Globals._pnSettingsSessionStorageKey,
        )
    }

    // region General
    public async _getDomainSettings(): Promise<TimestampedDataValue<DomainSettings> | null> {
        const req = await this._getTimestampedData<DomainSettings>(SettingsStorageKey.DOMAIN_SETTINGS, {
            preferredStores: settingsDocumentStores,
        })
        return req.unwrapOrNull()
    }

    /**
     * Persists the merged document and, alongside it, the config half under
     * {@link SettingsStorageKey.domainConfig}.
     *
     * The second write is not redundant. A service worker installed before this SDK version
     * keeps running its own code until it updates, and it reads the config from that key in
     * IndexedDB to recover the VAPID key on `pushsubscriptionchange`. Writing it here rather
     * than leaving it to callers means the worker's copy cannot silently stop being refreshed.
     */
    public async _setDomainSettings(settings: DomainSettings): Promise<DomainSettings | null> {
        const [req] = await Promise.all([
            this._setData<DomainSettings>(SettingsStorageKey.DOMAIN_SETTINGS, settings, {
                preferredStores: settingsDocumentStores,
            }),
            this._setDomainConfig(settings.config),
        ])

        return req.unwrapOrNull()
    }

    public async _getDomainConfig(): Promise<TimestampedDataValue<DomainConfig> | null> {
        const req = await this._getTimestampedData<DomainConfig>(SettingsStorageKey.domainConfig, {
            preferredStores: idbAndMemStoresOnly,
        })
        return req.unwrapOrNull()
    }

    public async _setDomainConfig(config: DomainConfig): Promise<DomainConfig | null> {
        const req = await this._setData<DomainConfig>(SettingsStorageKey.domainConfig, config, {
            preferredStores: idbAndMemStoresOnly,
        })
        return req.unwrapOrNull()
    }

    public async _getCachedEvents(): Promise<TrackedEvent[]> {
        const req = await this._getData<TrackedEvent[]>(SettingsStorageKey.eventsCache, {
            preferredStores: idbAndMemStoresOnly,
        })
        return req.unwrapOr([])
    }

    public async _setCachedEvents(events: TrackedEvent[]): Promise<TrackedEvent[]> {
        const req = await this._setData(SettingsStorageKey.eventsCache, events, {
            preferredStores: idbAndMemStoresOnly,
        })
        return req.unwrapOr([])
    }

    public async _getWriteAheadEvents(): Promise<TrackedEvent[]> {
        const req = await this._getData<TrackedEvent[]>(SettingsStorageKey.writeAheadEvents, {
            preferredStores: writeAheadEventStores,
        })
        return req.unwrapOrNull() ?? []
    }

    /**
     * Adds `event` by id to whatever is already stored. A tab that never saw an event does not erase
     * it, but two tabs adding at the same instant can still lose one of the two.
     */
    public async _addWriteAheadEvent(event: TrackedEvent): Promise<boolean> {
        return this._changeWriteAheadEventsInTurn(async () => {
            const stored = await this._getWriteAheadEvents()
            const next = [...stored.filter((storedEvent) => storedEvent.event_id !== event.event_id), event]
            const req = await this._setData(SettingsStorageKey.writeAheadEvents, next, {
                preferredStores: writeAheadEventStores,
            })
            return req.isOk()
        })
    }

    public async _removeWriteAheadEvent(eventId: string): Promise<void> {
        return this._changeWriteAheadEventsInTurn(async () => {
            const stored = await this._getWriteAheadEvents()
            await this._setData(
                SettingsStorageKey.writeAheadEvents,
                stored.filter((storedEvent) => storedEvent.event_id !== eventId),
                { preferredStores: writeAheadEventStores },
            )
        })
    }

    private _changeWriteAheadEventsInTurn<T>(change: () => Promise<T>): Promise<T> {
        const result = this.writeAheadEventsChange.then(change)
        // A failed change rejects only its own caller; the next one still runs.
        this.writeAheadEventsChange = result.catch(() => undefined)
        return result
    }
    // endregion

    // region Prompts
    public async _getViewedPromptOccurrencesCount(): Promise<number> {
        const map = await this._getViewedPromptOccurrencesMap()
        return reduceMap(map, (ttl, [, record]) => (ttl += record.length), 0)
    }

    public async _getViewedPromptOccurrencesMap(): Promise<Map<number, number[]>> {
        const ttl = await this._getData<Map<number, number[]>>(SettingsStorageKey.PROMPTS_OCCURRENCES_MAP, {
            preferredStores: idbAndMemStoresOnly,
        })
        return ttl.unwrapOr(new Map())
    }

    public async _dropViewedPromptOccurrencesNotInInterval(intervalMs: number): Promise<void> {
        const current = await this._getViewedPromptOccurrencesMap()

        const next = reduceMap(current, (reduced, [id, values]) => {
            const occurrences = values.filter((time) => {
                return Math.abs(timeIntervalFromNow(time)) <= intervalMs
            })
            if (occurrences.length > 0) {
                reduced.push([id, occurrences])
            }
            return reduced
        })

        await this._setData(SettingsStorageKey.PROMPTS_OCCURRENCES_MAP, next)
    }

    public async _incrementViewedOccurrencesForPromptId(id: number): Promise<void> {
        const occurrences = await this._getViewedPromptOccurrencesMap()

        occurrences.set(id, [new Date().getTime(), ...(occurrences.get(id) ?? [])])

        await this._setData(SettingsStorageKey.PROMPTS_OCCURRENCES_MAP, occurrences)
    }

    public async _getLastViewedPromptId(): Promise<number | null> {
        const req = await this._getData<number>(SettingsStorageKey.PROMPTS_LAST_VIEWED, {
            preferredStores: idbAndMemStoresOnly,
        })
        return req.unwrapOrNull()
    }

    public async _setLastViewedPromptId(id: number): Promise<number | null> {
        const req = await this._setData(SettingsStorageKey.PROMPTS_LAST_VIEWED, id, {
            preferredStores: idbAndMemStoresOnly,
        })
        return req.unwrapOrNull()
    }

    public async _setLastMatchedPromptId(promptId: number | null): Promise<number | null> {
        const req =
            promptId === null
                ? await this._removeData(SessionStorageKey.SESSION_READY_PROMPT, {
                      preferredStores: sessionStoresOnly,
                  })
                : await this._setData<number>(SessionStorageKey.SESSION_READY_PROMPT, promptId, {
                      preferredStores: sessionStoresOnly,
                  })
        return req.unwrapOrNull()
    }

    public async _getLastMatchedPromptId(): Promise<number | null> {
        const prompt = await this._getData<number>(SessionStorageKey.SESSION_READY_PROMPT, {
            preferredStores: sessionStoresOnly,
        })
        return prompt.unwrapOrNull()
    }
    // endregion
}
